import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import Loader from 'Components/Loader'
import DialogUIModal from 'Components/UIComponents/DialogUIModal'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import Validation from 'Utils/Validation'

const EditCountryWiseProductionReport = ({ setLoading, showEditModal, setShowEditModal, rowData, onSuccess, countryOptions = [] }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [buttonDisabled, setButtonDisabled] = useState(true)
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {} })

  useEffect(() => {
    if (rowData) {
      const resolvedCountryId = rowData?.countryId ?? rowData?.idCountry

      setFormData({
        ...rowData,
        countryId: resolvedCountryId !== undefined && resolvedCountryId !== null ? Number(resolvedCountryId) : null
      })
    }
    setErrors({})
    setButtonDisabled(true)
  }, [rowData, showEditModal])

  if (!rowData) return null

  const handleDismiss = () => {
    setShowDialog({ ...showDialog, showModal: false })
  }

  const handleChange = (value, field) => {
    if (field === 'countryId') {
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }))
    } else if (Validation.numberDotPlusAndMinusValidation(value) || value === '') {
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }))
    }

    setButtonDisabled(false)
    setErrors((prev) => ({
      ...prev,
      [field]: ''
    }))
  }

  const fieldLabels = {
    countryId: 'Country',
    year: 'Year',
    totalProduction: 'Total Production',
    arabica: 'Arabica',
    robusta: 'Robusta',
    exports: 'Exports',
    consumption: 'Consumption'
  }

  const handleBlur = (field) => {
    if (!String(formData[field] ?? '').trim()) {
      setErrors((prev) => ({
        ...prev,
        [field]: `${fieldLabels[field] || field} ${t('IS_REQUIRED')}`
      }))
    }
  }

  const checkFieldsEmpty = () => {
    const requiredFields = ['countryId', 'year', 'totalProduction', 'arabica', 'robusta', 'exports', 'consumption']

    return requiredFields.every((field) => String(formData[field] ?? '').trim() !== '')
  }

  const closeEditModal = () => {
    setShowEditModal(false)
    setErrors({})
    setButtonDisabled(true)
  }

  const updateProductionReport = async () => {
    handleDismiss()
    setLoading(true)

    try {
      const { id } = rowData

      const payload = {
        countryId: Number(formData.countryId),
        year: String(formData.year),
        totalProduction: Number(formData.totalProduction),
        arabica: Number(formData.arabica),
        robusta: Number(formData.robusta),
        exports: Number(formData.exports),
        consumption: Number(formData.consumption)
      }

      const response = await apiAdapterCoffeeWeb.updateCountryWiseProductionReport({ id }, payload)

      if (response?.data?.statusCode === 200 || response?.status === 200) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('REPORT_DATA_UPDATED_SUCCESSFULLY'),
          onHide: handleDismiss,
          onSuccess: handleDismiss
        })
        setShowEditModal(false)
        onSuccess?.()
      }
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('SORRY_COULD_NOT_UPDATE_REPORT_DATA_PLEASE_TRY_AGAIN_LATER'),
        onRetry: updateProductionReport,
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const selectedCountryId = formData?.countryId !== undefined && formData?.countryId !== null ? Number(formData.countryId) : null
  const effectiveCountryOptions = (() => {
    if (selectedCountryId === null) return countryOptions

    const countryExists = countryOptions.some((country) => Number(country?.id) === selectedCountryId)

    if (countryExists) return countryOptions

    return [
      ...countryOptions,
      {
        id: selectedCountryId,
        nicename: rowData?.countryName || `Country ${selectedCountryId}`,
        iso3: ''
      }
    ]
  })()

  const renderForm = () => (
    <div className="production-country-dynamic-edit-form">
      <div className="edit-input-form">
        <div className="input-wrapper">
          <span className="p-float-label">
            <Dropdown inputId="edit-production-country-id" value={selectedCountryId} options={effectiveCountryOptions} optionLabel="nicename" optionValue="id" onChange={(event) => handleChange(event.target.value, 'countryId')} onBlur={() => handleBlur('countryId')} className={`input-fields ${errors.countryId ? 'error-fields' : ''}`} data-testid="input-countryId" />
            <label htmlFor="edit-production-country-id" className={`field-label ${errors.countryId ? 'error-field-label' : ''}`}>
              {t('COUNTRY_NAME')}
              <span className="required-text-field">*</span>
            </label>
          </span>
          <div className="error-message">{errors.countryId && <small>{errors.countryId}</small>}</div>
        </div>
        {['year', 'totalProduction', 'arabica', 'robusta', 'exports', 'consumption'].map((field, index) => (
          <div key={index} className="input-wrapper">
            <span className="p-float-label">
              <InputText id={`${field}-${index}`} value={formData[field] ?? ''} onChange={(event) => handleChange(event.target.value, field)} onBlur={() => handleBlur(field)} className={`input-fields ${errors[field] ? 'error-fields' : ''}`} data-testid={`input-${field}`} />
              <label htmlFor={`${field}-${index}`} className={`field-label ${errors[field] ? 'error-field-label' : ''}`}>
                {fieldLabels[field]}
                <span className="required-text-field">*</span>
              </label>
            </span>
            <div className="error-message">{errors[field] && <small>{errors[field]}</small>}</div>
          </div>
        ))}
      </div>
    </div>
  )

  const footer = () => (
    <div className="edit-country-production-footer">
      <ButtonComponent type={'cancel'} onClick={closeEditModal} />
      <ButtonComponent type={'submit'} onClick={updateProductionReport} disabled={buttonDisabled || !checkFieldsEmpty()} />
    </div>
  )

  return (
    <>
      <DialogUIModal visible={showEditModal} onHide={closeEditModal} header={t('EDIT_COUNTRY_WISE_PRODUCTION_REPORT')} footer={footer} body={renderForm} className="country-wise-production-edit-modal" />
      <DialogModal {...showDialog} onDismiss={handleDismiss} />
    </>
  )
}

export default Loader(EditCountryWiseProductionReport)
